import { Chip, Paper, Typography, useMediaQuery } from "@material-ui/core"

import { theme } from "../../shared/theme"
import { AthletePhoto } from "../../components/athlete/AthletePhoto"
import { AwardWreathWithCaption } from "../../components/award/AwardLaurelWithCaption"
import { EntryDivisionAward } from "../../types.results.reignite"
import { profileUserImageUrl } from "../../utils/profileImageUrl"
import { msToTime } from "../../utils/msToTime"

import { useCardStyles, usePerformanceItemStyles } from "./styles"

const Skeleton = ({ className }: { className: string }) => {
  const styles = useCardStyles({ isPending: true })

  return (
    <div className={`${styles.skeleton} ${className}`} />
  )
}

type PerformanceItemProps = {
  className?: string
  isActive?: boolean
  label: string
  showLabel?: boolean
  value?: string | null
  chip?: {
    label: string
    color: string
  }
}

const PerformanceItem = ({
  className,
  isActive,
  label,
  showLabel = true,
  value,
  chip,
}: PerformanceItemProps) => {
  const styles = usePerformanceItemStyles({ chipBackground: chip?.color, isActive })

  return (
    <div className={`${className} ${styles.wrapper}`}>
      <div className={styles.container}>
        {showLabel && (
          <Typography className={styles.label}>
            {label}
          </Typography>
        )}

        {typeof value !== 'undefined' && !value && (
          <Skeleton className={styles.skeletonValue} />
        )}

        {!!value && (
          <Typography className={styles.value}>
            {value}
          </Typography>
        )}

        {chip && (
          <Chip
            label={chip.label}
            size='small'
            className={styles.chip}
          />
        )}
      </div>
    </div>
  )
}

type AwardCardProps = {
  award: EntryDivisionAward
  status: 'pending' | 'active' | 'finished'
  title: string
}

const AwardCard = ({ award, status, title, }: AwardCardProps) => {
  const isPending = status === 'pending'
  const isActive = status === 'active'
  const isFinished = status === 'finished'

  const profPicUrl = profileUserImageUrl(award.racerId, 't', !!award.racerId, true);

  const isMobile = !useMediaQuery(theme.breakpoints.up('sm'));

  const styles = useCardStyles({ isPending })

  return (
    <Paper className={styles.container}>
      <div>
        <AwardWreathWithCaption
          caption={award.gender}
          isPending={isPending}
          place={award.place}
        />

        <Typography className={styles.label}>
          {title} {(isPending && '(pending)') || (isFinished && 'Winner') || ''}
        </Typography>
      </div>

      <div>
        <div className={styles.athleteNameWrapper}>
          <>
            <div className={styles.athleteAvatar}>
              <AthletePhoto
                isMember={!!award.racerId}
                size='Thumb'
                photo={profPicUrl}
                name={award.displayName}
              />
            </div>

            <Typography className={styles.athleteName}>
              {award.displayName}
            </Typography>
          </>
        </div>
        <>
          <Typography className={styles.athleteInfo}>
            {award.gender} {award.age}<span>|</span>#{award.bib}
          </Typography>
          <Typography className={styles.athleteInfo}>
            {award.location?.locality}, {award.location?.region}
          </Typography>
        </>
      </div>

      <div className={styles.performanceSection}>
        {isActive && (
          <PerformanceItem
            className={styles.performanceStatusColumn}
            label='STATUS'
            showLabel={!isMobile}
            chip={{ label: 'On Course', color: '#ED6C02' }}
          />
        )}

        {!isActive && !isMobile && (
          <PerformanceItem
            label='PACE'
            value={'--:--'}
          />
        )}

        <PerformanceItem
          label='TIME'
          showLabel={!(isActive && isMobile)}
          isActive={isActive}
          value={msToTime(award.timeInMillis)}
        />
      </div>
    </Paper>
  )
}

export { AwardCard }